import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { RoteiroScriptView } from "@/components/shared/RoteiroScriptView";
import { toast } from "sonner";
import { friendlyError, toastError } from "@/lib/userMessages";
import {
  Plus,
  Copy,
  Sparkle,
  VideoCamera,
  InstagramLogo,
  Megaphone,
  FilmSlate,
  ArrowClockwise,
  CaretRight,
  CheckCircle,
  Warning,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { useGenerateRoteiro } from "@/features/roteiros/hooks/useGenerateRoteiro";
import { useRoteiroHistory } from "@/features/roteiros/hooks/useRoteiroHistory";
import { useDeleteRoteiro } from "@/features/roteiros/hooks/useDeleteRoteiro";
import { useRenameRoteiro } from "@/features/roteiros/hooks/useRenameRoteiro";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { InlineEditText } from "@/components/shared/InlineEditText";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { Checkbox } from "@/components/ui/checkbox";
import type {
  RoteiroType,
  ObjetivoRoteiro,
  GenerateRoteiroInput,
} from "@/features/roteiros/types";
import { PageStateGate } from "@/components/states/PageStateGate";
import {
  normalizeObjetivoLabel,
  normalizeTypeLabel,
  parseRoteiroPayload,
} from "@/features/roteiros/parseRoteiro";
import { isTopLevelRoteiroJob } from "@/features/roteiros/historyGrouping";
import { HelpButton } from "@/components/ui/help-button";

type TemplateIcon = typeof Sparkle;

const typeIcons: Record<string, { icon: TemplateIcon; color: string }> = {
  Reels: { icon: VideoCamera, color: "text-pink-400 bg-pink-500/10" },
  Stories: { icon: InstagramLogo, color: "text-purple-400 bg-purple-500/10" },
  Anúncio: { icon: Megaphone, color: "text-yellow-400 bg-warning/10" },
};

const ROTEIRO_TYPES: RoteiroType[] = ["Reels", "Stories", "Anúncio"];

const objetivoOptions: { value: ObjetivoRoteiro; label: string; description: string }[] = [
  { value: "educativo", label: "Educativo", description: "Ensinar e informar" },
  { value: "engajamento", label: "Engajamento", description: "Curtidas, comentários, salvos" },
  { value: "conversão", label: "Conversão", description: "Atrair consultas" },
  { value: "autoridade", label: "Autoridade", description: "Construir credibilidade" },
];

export default function Roteiros() {
  usePageTitle("Roteiros");
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const currentUser = {
    name: user?.name ?? "",
    specialty: user?.specialty ?? "",
  };

  const [dialogOpen, setDialogOpen] = useState(false);

  // AI generate form state
  const [formData, setFormData] = useState<GenerateRoteiroInput>({
    topic: "",
    type: "Reels",
    objetivo: "educativo",
    details: "",
  });
  // After first generation, lock topic/type/objetivo for variant mode
  const [variantContext, setVariantContext] = useState<Pick<GenerateRoteiroInput, "topic" | "type" | "objetivo"> | null>(null);

  const generateMutation = useGenerateRoteiro();
  const historyQuery = useRoteiroHistory();
  const deleteRoteiro = useDeleteRoteiro();
  const renameRoteiro = useRenameRoteiro();
  const visibleJobs = useMemo(
    () => (historyQuery.data ?? []).filter(isTopLevelRoteiroJob),
    [historyQuery.data],
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(visibleJobs.map((job) => job.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [visibleJobs]);

  // Track the last generated script separately so we can show it after success
  const generatedScript = generateMutation.data?.result ?? null;

  function handleGenerate() {
    const payload: GenerateRoteiroInput = variantContext
      ? { ...variantContext, details: formData.details }
      : formData;
    if (!payload.topic.trim()) {
      toast.error("Informe o tema do roteiro antes de gerar.");
      return;
    }
    generateMutation.mutate(payload, {
      onError: (err) => {
        toastError(err, "Não foi possível gerar o roteiro. Tente novamente.");
      },
      onSuccess: (data) => {
        toast.success("Roteiro gerado com sucesso!");
        setVariantContext({ topic: payload.topic, type: payload.type, objetivo: payload.objetivo });
        setDialogOpen(false);
        setFormData({ topic: "", type: "Reels", objetivo: "educativo", details: "" });
        generateMutation.reset();
        const newJobId = data?.result?.jobId;
        if (newJobId) {
          navigate(`/roteiros/${newJobId}`);
        }
      },
    });
  }

  function handleCopy(text: string) {
    void navigator.clipboard.writeText(text).then(() => {
      toast.success("Roteiro copiado!");
    });
  }

  function toggleSelection(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => deleteRoteiro.mutateAsync(id)));
    const success = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - success;
    if (success > 0) toast.success(`${success} roteiro(s) excluído(s).`);
    if (failed > 0) toast.error(`${failed} roteiro(s) não puderam ser excluídos.`);
    setSelectedIds(new Set());
    setBulkDeleting(false);
  }

  function statusLabel(status: string) {
    if (status === "done") return "Concluído";
    if (status === "failed") return "Falhou";
    if (status === "pending" || status === "running") return "Processando";
    return status;
  }

  return (
    <AppLayout>
      <PageStateGate
        empty={{
          icon: <FilmSlate weight="duotone" className="h-6 w-6" aria-hidden="true" />,
          title: "Nenhum roteiro por aqui",
          description: "Crie seu primeiro roteiro para começar a produzir.",
        }}
      >
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-foreground">
                  Gerador de <span className="accent-text">Roteiros</span>
                </h1>
                <HelpButton
                  title="Gerador de Roteiros"
                  description="Conteúdo Instagram (Reels, Stories, Anúncios) personalizado pro seu posicionamento."
                >
                  <p>
                    A IA gera roteiros de conteúdo a partir do seu nicho,
                    avatar de cliente ideal e mecanismo único — campos
                    cadastrados no <strong>Perfil</strong>. Quanto mais
                    completo o perfil, melhor o roteiro.
                  </p>
                  <p>
                    <strong>Como usar:</strong> escolha o tipo (Reels, Story,
                    Anúncio), informe o tema/objeção e gere. Edite, copie e
                    leve pro IG.
                  </p>
                  <p className="text-muted-foreground">
                    Etapa do método: captação. Roteiros aqui ≠ Mecanismo de
                    Influência Máxima (esse fica em Posicionamento).
                  </p>
                </HelpButton>
              </div>
              <p className="text-muted-foreground mt-1">
                Roteiros personalizados para{" "}
                <span className="text-primary">{currentUser.specialty}</span>
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 glow-primary-sm">
                  <Plus weight="bold" className="w-4 h-4" />
                  Novo Roteiro
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Gerar Roteiro com IA</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">

                <div className="space-y-4">
                  {variantContext ? (
                    /* ── Variant mode: context locked ── */
                    <div className="bg-secondary/30 border border-border/30 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Contexto fixo</span>
                        <button
                          type="button"
                          onClick={() => {
                            setVariantContext(null);
                            setFormData((p) => ({ ...p, details: "" }));
                          }}
                          className="text-xs text-primary hover:underline"
                        >
                          Mudar contexto
                        </button>
                      </div>
                      <p className="text-sm font-medium text-foreground">{variantContext.topic}</p>
                      <div className="flex gap-2 flex-wrap">
                        {(() => {
                          const style = typeIcons[variantContext.type];
                          return (
                            <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-primary/10 text-primary border border-primary/20">
                              <style.icon weight="duotone" className="w-3 h-3" />
                              {variantContext.type}
                            </span>
                          );
                        })()}
                        <span className="text-xs px-2 py-1 rounded-md bg-secondary text-muted-foreground border border-border/50">
                          {objetivoOptions.find((o) => o.value === variantContext.objetivo)?.label}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Topic */}
                      <div>
                        <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                          Tema do roteiro <span className="text-destructive">*</span>
                        </label>
                        <Textarea
                          placeholder="Descreva o tema do roteiro (ex: sinais de que você precisa de uma cirurgia no joelho)"
                          value={formData.topic}
                          onChange={(e) => setFormData((p) => ({ ...p, topic: e.target.value }))}
                          className="min-h-[80px] resize-y"
                          disabled={generateMutation.isPending}
                        />
                      </div>

                      {/* Format / Type */}
                      <div>
                        <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                          Formato
                        </label>
                        <div className="flex gap-2" role="group" aria-label="Formato do roteiro">
                          {ROTEIRO_TYPES.map((t) => {
                            const style = typeIcons[t];
                            const active = formData.type === t;
                            return (
                              <button
                                key={t}
                                type="button"
                                disabled={generateMutation.isPending}
                                onClick={() => setFormData((p) => ({ ...p, type: t }))}
                                className={cn(
                                  "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors",
                                  active
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-card text-muted-foreground border-border/50 hover:text-foreground",
                                )}
                              >
                                <style.icon weight="duotone" className="w-4 h-4" />
                                {t}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Objetivo */}
                      <div>
                        <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                          Objetivo
                        </label>
                        <div className="grid grid-cols-2 gap-2" role="group" aria-label="Objetivo do roteiro">
                          {objetivoOptions.map((o) => {
                            const active = formData.objetivo === o.value;
                            return (
                              <button
                                key={o.value}
                                type="button"
                                disabled={generateMutation.isPending}
                                onClick={() => setFormData((p) => ({ ...p, objetivo: o.value }))}
                                className={cn(
                                  "flex flex-col items-start px-3 py-2.5 rounded-lg text-left border transition-colors",
                                  active
                                    ? "bg-primary/10 border-primary/40 text-foreground"
                                    : "bg-card text-muted-foreground border-border/50 hover:text-foreground",
                                )}
                              >
                                <span className="text-sm font-medium">{o.label}</span>
                                <span className="text-xs text-muted-foreground mt-0.5">{o.description}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Details — always visible */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                      {variantContext ? "Novo ângulo ou informações adicionais" : "Mais detalhes"}
                    </label>
                    <Textarea
                      placeholder="Coloque aqui todas as informações e contexto adicional sobre esse roteiro. Quanto mais informações fornecer, melhor será o resultado."
                      value={formData.details}
                      onChange={(e) => setFormData((p) => ({ ...p, details: e.target.value }))}
                      className="min-h-[100px] resize-y"
                      disabled={generateMutation.isPending}
                    />
                  </div>
                </div>

                <Button
                  className="w-full gap-2 glow-primary-sm"
                  onClick={handleGenerate}
                  disabled={generateMutation.isPending || (!variantContext && !formData.topic.trim())}
                >
                  {generateMutation.isPending ? (
                    <>
                      <ArrowClockwise weight="bold" className="w-4 h-4 animate-spin" />
                      Gerando…
                    </>
                  ) : (
                    <>
                      <Sparkle weight="duotone" className="w-4 h-4" />
                      {variantContext ? "Gerar Variação" : "Gerar Roteiro"}
                    </>
                  )}
                </Button>

                {/* Error state */}
                {generateMutation.isError && (
                  <div className="flex items-start gap-3 bg-destructive/15 border border-destructive/30 rounded-lg p-4">
                    <Warning
                      weight="duotone"
                      className="w-5 h-5 text-destructive shrink-0 mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-destructive">Erro ao gerar roteiro</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {friendlyError(generateMutation.error, "Não foi possível gerar o roteiro. Tente novamente.")}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 text-destructive border-destructive/30 hover:bg-destructive/15"
                      onClick={handleGenerate}
                    >
                      Tentar novamente
                    </Button>
                  </div>
                )}

                {/* Success: generated script */}
                {generatedScript && !generateMutation.isPending && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle weight="duotone" className="w-5 h-5 text-green-400" />
                        <span className="text-sm font-medium text-foreground">Roteiro gerado</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-muted-foreground hover:text-primary"
                          onClick={() => handleCopy(generatedScript.script)}
                        >
                          <Copy weight="regular" className="w-4 h-4" />
                          Copiar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-muted-foreground hover:text-primary"
                          onClick={handleGenerate}
                          disabled={generateMutation.isPending}
                        >
                          <ArrowClockwise weight="regular" className="w-4 h-4" />
                          Regenerar
                        </Button>
                      </div>
                    </div>
                    <RoteiroScriptView script={generatedScript.script} />
                  </div>
                )}
                </div>
              </DialogContent>
            </Dialog>
          </div>


          {/* ── Lista de roteiros ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-medium text-muted-foreground">
                Roteiros recentes
                {visibleJobs.length > 0 && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {visibleJobs.length}
                  </Badge>
                )}
              </h2>
            </div>
            <BulkActionsBar
              totalCount={visibleJobs.length}
              selectedCount={selectedIds.size}
              allSelected={visibleJobs.length > 0 && selectedIds.size === visibleJobs.length}
              selectionMode={selectionMode}
              deleting={bulkDeleting}
              entityPluralLabel="roteiros"
              onToggleSelectionMode={(enabled) => {
                setSelectionMode(enabled);
                if (!enabled) setSelectedIds(new Set());
              }}
              onToggleSelectAll={(checked) => {
                if (!checked) {
                  setSelectedIds(new Set());
                  return;
                }
                setSelectedIds(new Set(visibleJobs.map((job) => job.id)));
              }}
              onClearSelection={() => setSelectedIds(new Set())}
              onConfirmDelete={handleBulkDelete}
            />

            {historyQuery.isPending && (
              <div className="flex items-center justify-center py-8">
                <ArrowClockwise
                  weight="bold"
                  className="w-5 h-5 animate-spin text-muted-foreground"
                />
              </div>
            )}
            {historyQuery.isError && (
              <div className="bg-destructive/15 border border-destructive/30 rounded-lg p-4">
                <p className="text-sm text-destructive">
                  {friendlyError(historyQuery.error, "Não foi possível carregar seus roteiros. Tente atualizar a página.")}
                </p>
              </div>
            )}
            {historyQuery.data && visibleJobs.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">Nenhum roteiro ainda. Clique em Novo Roteiro para criar.</p>
              </div>
            )}

            <div className="space-y-2">
              {visibleJobs.map((job) => {
                const result = Array.isArray(job.ai_job_results)
                  ? job.ai_job_results[0]
                  : job.ai_job_results;
                const topic = result?.analysis_json?.topic;
                const script = typeof result?.analysis_json?.script === "string"
                  ? result.analysis_json.script
                  : "";
                const parsed = script ? parseRoteiroPayload(script) : null;
                const persisted = job.request_metadata?.normalized ?? null;
                const typeLabel =
                  normalizeTypeLabel(persisted?.type) ??
                  parsed?.typeLabel ??
                  (typeof result?.analysis_json?.type === "string"
                    ? normalizeTypeLabel(result.analysis_json.type)
                    : null);
                const objetivoLabel =
                  normalizeObjetivoLabel(persisted?.objective) ??
                  parsed?.objetivoLabel ??
                  (typeof result?.analysis_json?.objective === "string"
                    ? normalizeObjetivoLabel(result.analysis_json.objective)
                    : null);
                const date = new Date(job.created_at).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                });
                return (
                  <div
                    key={job.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/roteiros/${job.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/roteiros/${job.id}`);
                      }
                    }}
                    className="w-full text-left bg-card border border-border/50 rounded-xl p-4 hover:bg-secondary/20 hover:border-primary/40 transition-colors group cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      {selectionMode ? (
                        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(job.id)}
                            onCheckedChange={(v) => toggleSelection(job.id, Boolean(v))}
                            aria-label="Selecionar roteiro"
                            disabled={bulkDeleting}
                          />
                        </div>
                      ) : null}
                      <div
                        className={cn(
                          "p-2 rounded-lg shrink-0",
                          job.status === "done"
                            ? "bg-green-500/10"
                            : job.status === "failed"
                              ? "bg-destructive/15"
                              : "bg-warning/10",
                        )}
                      >
                        <FilmSlate
                          weight="duotone"
                          className={cn(
                            "w-4 h-4",
                            job.status === "done"
                              ? "text-green-400"
                              : job.status === "failed"
                                ? "text-destructive"
                                : "text-yellow-400",
                          )}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <InlineEditText
                          value={topic ?? `Roteiro ${job.id.slice(0, 8)}`}
                          onSave={async (name) => {
                            try {
                              await renameRoteiro.mutateAsync({ id: job.id, topic: name });
                              toast.success("Renomeado com sucesso.");
                            } catch (err) {
                              toastError(err, "Não foi possível renomear. Tente novamente.");
                              throw err;
                            }
                          }}
                          className="text-sm font-medium text-foreground"
                          disabled={bulkDeleting}
                        />
                        <p className="text-xs text-muted-foreground mt-0.5">{date}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {typeLabel ? (
                            <Badge variant="secondary" className="text-[11px]">
                              {typeLabel}
                            </Badge>
                          ) : null}
                          {objetivoLabel ? (
                            <Badge variant="outline" className="text-[11px]">
                              {objetivoLabel}
                            </Badge>
                          ) : null}
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[11px]",
                              job.status === "done"
                                ? "border-green-500/30 text-green-400"
                                : job.status === "failed"
                                  ? "border-destructive/30 text-destructive"
                                  : "border-warning/30 text-yellow-400",
                            )}
                          >
                            {statusLabel(job.status)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <DeleteEntityButton
                          entityLabel="roteiro"
                          confirmDescription="O roteiro será removido do seu histórico permanentemente."
                          onConfirm={async () => {
                            try {
                              await deleteRoteiro.mutateAsync(job.id);
                              toast.success("Roteiro excluído.");
                            } catch (err) {
                              toastError(err, "Não foi possível excluir o roteiro. Tente novamente.");
                              throw err;
                            }
                          }}
                          disabled={bulkDeleting}
                          data-testid={`roteiro-delete-${job.id}`}
                        />
                        <CaretRight
                          weight="bold"
                          className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </PageStateGate>
    </AppLayout>
  );
}
